import React, { Fragment, useEffect, useState } from 'react'
import { useHistory } from 'react-router-dom'
import { actionTypes as appActionTypes, selectors as appSelectors } from '../features/app'
import { selectors as errorSelectors } from '../features/error'
import { selectors as vehicleSelectors } from '../features/vehicle'
import { selectors as valuationSelectors } from '../features/valuation'
import { selectors as dealerSelectors } from '../features/dealer'
import { useSelector, useDispatch } from 'react-redux'
import { getFormattedKms, getFormattedMoney, getMonths, getFormattedOpeningHours, scrollToBeginning } from '../helpers'
import { baseName, Routes } from '../types.d'
import { actionTypes as errorActionTypes } from '../features/error'
import { getAcceptOffer } from '../api'
import { IError, IErrorButton } from '../features/error/types'
import SendEvent from '../analytics'
import { EAppVersion } from '../features/app/types'

type Props = {
  oid?: string
};

export const Offer: React.FC<Props> = ({ oid }) => {
  const history = useHistory();
  const dispatch = useDispatch();

  const [isInit, setIsInit] = useState(false);
  const version = useSelector(appSelectors.getAppVersion);
  const subStep = useSelector(appSelectors.getAppSubStep);
  const isLoading = useSelector(appSelectors.getAppIsLoading);
  const isError = useSelector(errorSelectors.getErrorTitle);
  const family = useSelector(vehicleSelectors.getVehicleFamily);
  const image = useSelector(vehicleSelectors.getVehicleImage);
  const year = useSelector(vehicleSelectors.getVehicleYear);
  const make = useSelector(vehicleSelectors.getVehicleMake);
  const model = useSelector(vehicleSelectors.getVehicleModel);
  const description = useSelector(vehicleSelectors.getVehicleDescriptionLine);
  const rego = useSelector(vehicleSelectors.getVehicleRego);
  const auststate = useSelector(vehicleSelectors.getVehicleAustState)
  const kms = useSelector(vehicleSelectors.getVehicleKms);
  const valuation = useSelector(valuationSelectors.getValuationValuation);
  const locationName = useSelector(dealerSelectors.getDealerLocationName);
  const phone = useSelector(dealerSelectors.getDealerPhone);
  const openingHours = useSelector(dealerSelectors.getDealerOpeningHours);
  const website = useSelector(dealerSelectors.getDealerWebsite);
  const address = useSelector(dealerSelectors.getDealerAddress);
  const expiry = useSelector(appSelectors.getAppExpiry);
  const redbookCode = useSelector(vehicleSelectors.getVehicleRedbookCode);
  const offerId = useSelector(appSelectors.getAppOfferId);
  const vehicleTitle = useSelector(vehicleSelectors.getVehicleTitle);
  const vehicleDescriptionLine = useSelector(vehicleSelectors.getVehicleDescriptionLine);
  const vehicleSecondaryLine = useSelector(vehicleSelectors.getVehicleSecondaryLine);
  const vehicleTertiaryLine = useSelector(vehicleSelectors.getVehicleTertiaryLine);

  const expiryDate = new Date(expiry);
  const expiryFormatted = `${expiryDate.getDate()} ${getMonths[expiryDate.getMonth()]} ${expiryDate.getFullYear()}`;

  const handleStartOverClick = () => {
    SendEvent({
      event: 'sendEvent',
      eventCategory: 'User Interactions',
      eventAction: `Instant Offer - offer`,
      eventLabel: `Start over - button click`,
      eventValue: 0,
      nonInteraction: false
    });
    setTimeout(() => {
      location.pathname = baseName + Routes.Specs;
    },10);
  };
  const handleInterestClick = async () => {
    SendEvent({
      event: 'sendEvent',
      eventCategory: 'User Interactions',
      eventAction: `Instant Offer - offer`,
      eventLabel: `Submit interest - button click`,
      eventValue: 0,
      nonInteraction: false
    });
    dispatch({ type: appActionTypes.SET_APP, payload: { isLoading: true } });
    const r = await getAcceptOffer(offerId.toString(), redbookCode);
    if (!r.errormessage && r.result){
      dispatch({ type: appActionTypes.SET_APP, payload: { subStep: 1 } });
      scrollToBeginning();
      SendEvent({
        event: 'sendVirtualPageView',
        pageType: 'instantoffer',
        viewSection: 'interest',
        viewName: 'offer',
      });
    }
    else {
      const btns: Array<IErrorButton> = [
        {
          title: 'Try again', 
          classes: 'button-solid', 
          callback: () => handleInterestClick(), 
          clearErrorOnClick: true
        }, 
        {
          title: 'Back to registration', 
          classes: 'button-outline', 
          callback: () => { location.pathname = baseName + Routes.Specs }, 
          clearErrorOnClick: false
        }
      ]
      const err: IError = { 
        icon: require('../assets/images/exclamation.svg'),
        title: 'Sorry, we seem to be experiencing some technical difficulties, please try again later.', 
        buttons: btns
      };
      dispatch({ type: errorActionTypes.SET_ERROR, payload: err });
    }
    dispatch({ type: appActionTypes.SET_APP, payload: { isLoading: false } });
  };

  useEffect(() => {
    if (!isInit){
      setIsInit(true);
      
      if (oid) {
        dispatch({ type: appActionTypes.SET_APP, payload: { step: 3, subStep: 1 } });
        dispatch({ type: appActionTypes.SET_APP, payload: { isLoading: false } });
        scrollToBeginning();
        return;
      };

      if (!offerId) history.replace(Routes.Specs);
      dispatch({ type: appActionTypes.SET_APP, payload: { step: 3, subStep: 0 } });
      dispatch({ type: appActionTypes.SET_APP, payload: { isLoading: false } });
      scrollToBeginning();
      SendEvent({
        event: 'sendVirtualPageView',
        pageType: 'instantoffer',
        viewSection: 'valuation',
        viewName: 'offer',
        vehicle: vehicleTitle,
        valuation: valuation
      });
    }
  });

  return (
    <Fragment>
      <section className={`${subStep === 0 ? 'i-step-valuation' : 'i-step-confirmation'} i-step-all ${isLoading || isError ? 'i-loading' : ''}`}>
      {subStep === 0 ? <Fragment>
        <div className="i-banner">
            <div className="i-con">
                <div className="i-row i-step-all-row">
                    <div className="i-col">
                        {version == EAppVersion.a ? 
                          <h2>Great! Here&rsquo;s our offer for your {make} {family}</h2>
                          : <React.Fragment>
                            <h2>Great! We have emailed your {make} {family} valuation.</h2>
                            <p>A friendly team member will be in&nbsp;contact.</p>
                          </React.Fragment>
                        }
                    </div>
                    <div className="i-col">
                        <img src={image} alt={family} />
                    </div>
                </div>
            </div>
        </div>

        <div className="i-detail">
          <div className="i-con">
            <div className="i-row i-step-all-row">
              <div className="i-col">
                {version == EAppVersion.a ? 
                  <h2>{getFormattedMoney(valuation, false)}<sup>*</sup></h2>
                : null}
                <h3>{vehicleTitle}</h3>
                <p>
                    <strong>{vehicleDescriptionLine}</strong>
                    {vehicleSecondaryLine} {vehicleTertiaryLine}
                    <br /><span>{rego} ({auststate})</span>
                </p>
                <dl>
                    <div>
                        <dt>Offer Expiry</dt>
                        <dd>{expiryFormatted}</dd>
                    </div>
                    <div>
                        <dt>Buyer</dt>
                        <dd>{locationName}</dd>
                    </div>
                    <div>
                        <dt>Odometer</dt>
                        <dd>{getFormattedKms(parseInt(kms))} km</dd>
                    </div>
                </dl>
              </div>
              {version == EAppVersion.a ? 
              <div className="i-col">
                <h4>Are you interested in this offer?</h4>
                <div className="i-button-group">
                  <button className="button-solid" onClick={handleInterestClick}>Submit interest</button>
                  <button className="button-outline" onClick={handleStartOverClick}>Start over</button>   
                </div>
                <h4>Need more time?</h4>
                <p>We’ve also emailed you this offer, so if you need more time and would like to accept it later, you have until {expiryFormatted} to do&nbsp;so.</p>
              </div>
              : null}
            </div>
          </div>
        </div>
      </Fragment> : 
          <div className="i-con">
            <div className="i-row i-step-all-row">
              <div className="i-col">
                <h2>Success! You’ve accepted our offer</h2>
              </div>
              <div className="i-col">
                <h5>What happens next</h5>
                {/* level="3" */}
                <ol>
                  <li>One of our team members will call you to book an inspection.</li>
                  <li>Come into the dealer (with this email offer) for your inspection before finalising your offer.</li>
                  <li>At your in dealer appointment, we will confirm the final price based on our qualified inspection.</li>       
                </ol>
                
                <h5>Bring the following items to your onsite&nbsp;valuation:</h5>
                {/* level="3" */}
                <ul>
                  <li>Vehicle registration certificate</li>
                  <li>Your current driver’s licence</li>
                  <li>Insurance documentation (if available)</li>
                  <li>Service history documentation (if available)</li>
                  <li>Loan information (if available)</li>
                  <li>All sets of keys</li>
                  <li>Owner’s manual (if available)</li>
                  <li>Any spare tyres and/or tools</li>
                </ul>

                <h5>
                  {/* level="3" */}
                  <small>Contact us</small>
                  {locationName}
                </h5>
                <p>{address}</p>
                {true || openingHours.length ? 
                  <p dangerouslySetInnerHTML={{__html:getFormattedOpeningHours(openingHours)}}></p>
                : ''}
                <p>
                  <a href={`tel:${phone.replace(/\s/g,'')}`}>{phone}</a>
                </p>
                <div className="i-button-group">
                  <a href={website} className="button-solid">Back to {locationName}</a>
                </div>
              </div>
            </div>
          </div>
        }
      </section>
    </Fragment>
  )
}
